import React from 'react';
import { Box } from '@chakra-ui/react';
import { ArrowCta, FormCard, Heading, Text } from 'components/ui';
import { ThankYouStepRow } from 'components/ThankYouView/ThankYouStepRow';
import DownloadApp from 'components/ThankYouView/DownloadApp';
import { mergeSearchParams } from 'utils/searchParams';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReadAuthUserIdWithQueryParamsLookup } from '../../hooks/useReadAuthUserIdWithQueryParamsLookup';

type Props = {
  email?: string;
  isCollaborative?: boolean;
  hasOccurredMeeting?: boolean;
};

const ThankYouNoAppointment = ({
  email,
  isCollaborative,
  hasOccurredMeeting,
}: Props) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { userId } = useReadAuthUserIdWithQueryParamsLookup();

  return (
    <>
      <FormCard
        borderColor={'blue.100'}
        data-testid={'thank-you-no-appointment'}
      >
        <Heading textAlign={'left'} fontSize={'22px'} lineHeight={'33px'}>
          What to do next
        </Heading>

        {!hasOccurredMeeting && (
          <Box bg={'blue.100'} py={4} px={3} borderRadius={14}>
            <ThankYouStepRow
              step={1}
              title="Book your first visit"
              body={
                isCollaborative
                  ? 'A 45-minute video visit with an Ayble doctor to review your symptoms and start your plan.'
                  : 'A 30-minute video visit with a digestive health coach to review your symptoms and start your plan.'
              }
              children={
                <ArrowCta
                  mt={3}
                  maxW={'214px'}
                  onClick={() =>
                    navigate(
                      `/schedule-appointment${mergeSearchParams(search, { userId })}`,
                      { state: { userEmail: email } }
                    )
                  }
                >
                  Choose a time
                </ArrowCta>
              }
            />
          </Box>
        )}

        <ThankYouStepRow
          step={hasOccurredMeeting ? 1 : 2}
          title={'Get the app on your phone'}
          body={'You can do this after you book.'}
          children={<DownloadApp />}
        />
        {!hasOccurredMeeting && (
          <ThankYouStepRow
            step={3}
            title={
              email
                ? 'Log in with the email you just used'
                : 'Log in with the email you used to register'
            }
            children={
              email && (
                <Box
                  bg={'#E4EEF1'}
                  rounded={8}
                  px={'12px'}
                  py={'6px'}
                  alignSelf={'flex-start'}
                >
                  <Text
                    color={'blueGray.600'}
                    fontFamily={'body'}
                    fontSize={'13.5px'}
                    fontWeight={700}
                  >
                    {email}
                  </Text>
                </Box>
              )
            }
          />
        )}
        <ThankYouStepRow
          step={hasOccurredMeeting ? 2 : 4}
          title={'Start setting up your Care Guide'}
          body={
            'This takes about 5 minutes and helps your care team personalize your first appointment.'
          }
        />
        <ThankYouStepRow
          step={hasOccurredMeeting ? 3 : 5}
          title={'Message your care team anytime'}
          body={'Have a question before your visit? Send it right in the app.'}
        />
      </FormCard>
    </>
  );
};

export default ThankYouNoAppointment;
